import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, XCircle, Star, GraduationCap, BookMarked } from "lucide-react";
import { toggleFavorite, getFavorites } from "../lib/store";
import { findGlossaryTerms } from "../lib/glossaryMatch";

// Question posée -> l'utilisateur choisit une option -> la correction apparaît instantanément.
export function InteractiveQuestion({ q, index, onAnswered, testPrefix = "iq", onFavChange }) {
  const [selected, setSelected] = useState(null);
  const [validated, setValidated] = useState(false);
  const [fav, setFav] = useState(() => getFavorites(q.level).includes(q.id));

  const choose = (oi) => {
    if (validated) return;
    setSelected(oi);
    setValidated(true);
    onAnswered && onAnswered(oi === q.answer, q);
  };

  const toggleFav = () => {
    const added = toggleFavorite(q.level, q.id);
    setFav(added);
    onFavChange && onFavChange(q.id, added);
  };

  const terms = useMemo(
    () => (validated ? findGlossaryTerms(`${q.question} ${q.options[q.answer]}`) : []),
    [validated, q]
  );

  return (
    <div className="rounded-sm border border-border bg-card p-5" data-testid={`${testPrefix}-card-${index}`}>
      <div className="flex items-center justify-between mb-2 gap-2">
        <span className="font-mono text-xs text-primary">{q.id}</span>
        <div className="flex items-center gap-2">
          {typeof index === "number" && (
            <span className="font-mono text-xs text-muted-foreground">#{index + 1}</span>
          )}
          <button
            onClick={toggleFav}
            data-testid={`${testPrefix}-${index}-fav`}
            aria-label="Ajouter aux favoris"
            className={`transition-colors ${fav ? "text-warning" : "text-muted-foreground hover:text-warning"}`}
          >
            <Star size={18} fill={fav ? "currentColor" : "none"} />
          </button>
        </div>
      </div>
      <h3 className="font-display font-bold text-lg leading-snug">{q.question}</h3>

      <div className="mt-4 space-y-2">
        {q.options.map((opt, oi) => {
          const isCorrect = oi === q.answer;
          const isSel = oi === selected;
          let cls = "border-border hover:border-primary/60 hover:bg-secondary/40 cursor-pointer";
          let Icon = null;
          if (validated) {
            if (isCorrect) {
              cls = "border-success bg-success/12";
              Icon = CheckCircle2;
            } else if (isSel) {
              cls = "border-destructive bg-destructive/12";
              Icon = XCircle;
            } else {
              cls = "border-border opacity-60";
            }
          } else if (isSel) {
            cls = "border-primary bg-primary/12";
          }
          return (
            <button
              key={opt}
              disabled={validated}
              onClick={() => choose(oi)}
              data-testid={`${testPrefix}-${index}-option-${oi}`}
              className={`w-full text-left flex items-center gap-3 rounded-sm border px-4 py-3 text-sm transition-colors ${cls}`}
            >
              <span className={`font-mono text-xs w-5 h-5 grid place-items-center rounded-sm shrink-0 ${isSel && !validated ? "bg-primary text-primary-foreground" : "border border-border"}`}>
                {String.fromCharCode(65 + oi)}
              </span>
              <span className="flex-1">{opt}</span>
              {Icon && <Icon size={18} className={isCorrect ? "text-success" : "text-destructive"} />}
            </button>
          );
        })}
      </div>

      {validated && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="mt-4 space-y-3">
          <div className={`text-sm font-semibold ${selected === q.answer ? "text-success" : "text-destructive"}`}>
            {selected === q.answer ? "✓ Bonne réponse !" : "✗ Ce n'est pas ça."}
          </div>
          <div className="rounded-sm bg-success/10 border border-success/30 px-3 py-2 text-sm">
            <span className="text-muted-foreground">Bonne réponse : </span>
            <span className="font-medium text-foreground">{q.options[q.answer]}</span>
          </div>

          <details className="rounded-sm border border-border" data-testid={`${testPrefix}-${index}-understand`}>
            <summary className="cursor-pointer select-none px-3 py-2 text-sm font-semibold text-primary">
              Comprendre cette question
            </summary>
            <div className="px-3 pb-3 pt-1 space-y-3">
              <Link
                to={`/formation/${q.section}`}
                className="inline-flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <GraduationCap size={15} /> Réviser la leçon : {q.sectionName}
              </Link>
              {terms.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-muted-foreground">
                    <BookMarked size={13} /> Termes clés
                  </div>
                  {terms.map((t) => (
                    <div key={t.term} className="text-sm">
                      <span className="font-semibold">{t.term}</span>
                      <span className="text-muted-foreground"> — {t.short}</span>
                    </div>
                  ))}
                  <Link to="/glossaire" className="text-xs text-primary hover:underline">
                    Ouvrir le glossaire →
                  </Link>
                </div>
              )}
            </div>
          </details>
        </motion.div>
      )}
    </div>
  );
}
